import React, { useContext, useState } from 'react';
import { StackShim as Stack } from '@fluentui/react-migration-v8-v9';
import { Subtitle1, Body1, makeStyles, tokens, MessageBar } from '@fluentui/react-components';
import { useQuery } from '@apollo/client';
import StackColumn from '../../common/StackColumn';
import Card from '../../common/card/Card';
import ShimmeredDetailsListWrapper from '../../common/DetailsLists/ShimmeredDetailsListWrapper';
import { bodyContentContainer } from '../../../app/common/styles/CommonStyleObjects';
import FeatureFlagged from '../../common/featureFlagged/featureFlagged';
import getColumns from '../shared/EntityTerminationActionItemListPage.config';
import getStatToolOpenProcessesColumns, {
  getStatToolOpenProcesses,
} from './StatToolOpenProcesses.config';
import ReviewComponent from '../../reviews/ReviewComponent';
import FinanceIncompleteReviewApprovalPanel from './FinanceIncompleteReviewApprovalPanel';
import SAPDeactivationProgress from './SAPDeactivationProgress';
import {
  GET_FINANCE_INCOMPLETE_DETAILS,
  GET_FINANCE_INCOMPLETE_OPEN_PROCESSES,
  GET_FINANCE_INCOMPLETE_REVIEW,
  APPROVE_ENTITY_TERMINATION_FINANCE_INCOMPLETE_REVIEW,
  CREATE_ENTITY_TERMINATION_FINANCE_INCOMPLETE_REVIEW_COMMENT,
  ATTACH_ENTITY_TERMINATION_FINANCE_INCOMPLETE_REVIEW_DOCUMENT,
} from '../../../utils/statApi/EntityTerminationApi';
import {
  EntityTerminationUriSegment,
  FinanceIncompleteActionItemTemplate,
  IEntityTerminationActionItem,
  IFinanceIncompleteDetails,
} from '../types';
import FeatureFlagContext from '../../../utils/featureFlags/FeatureFlagContext';
import FeatureFlags from '../../../utils/featureFlags/FeatureFlags';
import hasAccess from '../../../utils/authorization/authorizationCheck';
import UserContext from '../../../utils/authorization/UserContext';
import { StatUser } from '../../../utils/statApi/UsersApi';

const useStyles = makeStyles({
  bottomSpacing: { marginBottom: '50px' },
  messageContainer: {
    paddingTop: '12px',
    paddingBottom: '12px',
    maxWidth: '640px',
  },
  secondaryText: {
    color: tokens.colorNeutralForeground3,
  },
});

interface IEntityTerminationFinanceIncompletePageProps {
  loading?: boolean;
  companyCode: string;
}

const EntityTerminationFinanceIncompletePage = (
  props: IEntityTerminationFinanceIncompletePageProps,
): JSX.Element => {
  const { loading, companyCode } = props;
  const classes = useStyles();

  const user = useContext(UserContext) as unknown as StatUser;
  const canViewProcesses = hasAccess(user?.permissions, 'ProcessesList:View');
  const featureFlags: FeatureFlags = useContext(FeatureFlagContext);
  const isFinanceIncompleteActive = !!featureFlags?.flags.EntityTerminationFinanceIncomplete;

  const {
    data: actionItemsData,
    loading: actionItemsLoading,
    error: actionItemsError,
  } = useQuery<{ financeIncompleteDetails: IFinanceIncompleteDetails }>(
    GET_FINANCE_INCOMPLETE_DETAILS,
    {
      variables: { companyCode },
      fetchPolicy: 'cache-and-network',
      skip: !isFinanceIncompleteActive,
    },
  );

  const financeIncompleteActionItems: IEntityTerminationActionItem[] =
    actionItemsData?.financeIncompleteDetails?.actionItems ?? [];
  const financeIncompleteProgress = actionItemsData?.financeIncompleteDetails?.progress;

  const [isReviewApprovalPanelOpen, setIsReviewApprovalPanelOpen] = useState<boolean>(false);

  const { data: reviewData, loading: reviewLoading } = useQuery(GET_FINANCE_INCOMPLETE_REVIEW, {
    variables: { companyCode },
    fetchPolicy: 'cache-and-network',
    skip: !isFinanceIncompleteActive,
  });

  const financeIncompleteReview = reviewData?.financeIncompleteReview;

  const { data: openProcessesData, loading: openProcessesLoading } = useQuery(
    GET_FINANCE_INCOMPLETE_OPEN_PROCESSES,
    {
      variables: { companyCode },
      fetchPolicy: 'cache-and-network',
      skip: !isFinanceIncompleteActive,
    },
  );

  const openProcesses = openProcessesData?.financeIncompleteOpenProcesses;

  const isStatToolActionItem = (item: IEntityTerminationActionItem): boolean =>
    item.description === FinanceIncompleteActionItemTemplate.StatToolOpenProcesses;
  const statToolActionItems = financeIncompleteActionItems.filter(isStatToolActionItem);
  const sapActionItems = financeIncompleteActionItems.filter((item) => !isStatToolActionItem(item));

  const is9000CompanyCode = companyCode?.startsWith('9000');

  const allActionItemsCompleted =
    is9000CompanyCode ||
    (financeIncompleteActionItems.length > 0 &&
      financeIncompleteActionItems.every(
        (item: IEntityTerminationActionItem) =>
          item.dateCompleted && (item.tasks || []).every((task) => task.completedDate),
      ));

  const approveDisabledTooltip = !allActionItemsCompleted
    ? 'All action items and tasks must be completed before this review can be approved.'
    : undefined;

  const statToolOpenProcesses = getStatToolOpenProcesses(openProcesses);

  return (
    <>
      <FeatureFlagged flagName="EntityTerminationFinanceIncomplete" showIfInactive>
        <div className={`${bodyContentContainer} lightBackground`}>
          <Card title="Finance Incomplete" isLoading={!!loading} headingLevel={2}>
            <Stack tokens={{ childrenGap: 8 }} className={classes.messageContainer}>
              <Subtitle1>This stage is coming soon.</Subtitle1>
              <Body1 className={classes.secondaryText}>
                The Finance Incomplete workflow is currently in development.
              </Body1>
            </Stack>
          </Card>
        </div>
      </FeatureFlagged>
      <FeatureFlagged flagName="EntityTerminationFinanceIncomplete">
        <div className={`${bodyContentContainer} lightBackground`}>
          {is9000CompanyCode && (
            <MessageBar intent="info" layout="multiline">
              9000x company codes are not assigned Finance Incomplete action items.
            </MessageBar>
          )}
          <Stack>
            <Stack
              horizontal
              wrap
              horizontalAlign="space-between"
              tokens={{ childrenGap: 20 }}
              className={classes.bottomSpacing}
            >
              <StackColumn>
                <SAPDeactivationProgress
                  progress={financeIncompleteProgress}
                  loading={actionItemsLoading}
                  error={actionItemsError}
                />
              </StackColumn>
              <StackColumn>
                <Card title="Stat Tool Open Processes" isLoading={!!loading} headingLevel={2}>
                  <ShimmeredDetailsListWrapper
                    columns={getStatToolOpenProcessesColumns(companyCode, canViewProcesses)}
                    items={statToolOpenProcesses}
                    enableShimmer={openProcessesLoading && !openProcesses}
                    singularListLabel="Open Process"
                    pluralListLabel="Open Processes"
                  />
                </Card>
              </StackColumn>
            </Stack>

            <Stack tokens={{ childrenGap: 20 }}>
              <Card
                title="Stat Tool Deactivation"
                isLoading={!!loading || actionItemsLoading}
                headingLevel={2}
              >
                <ShimmeredDetailsListWrapper
                  columns={getColumns(companyCode, EntityTerminationUriSegment.FinanceIncomplete)}
                  items={statToolActionItems}
                  singularListLabel="Action Item"
                  pluralListLabel="Action Items"
                />
              </Card>
              <Card
                title="SAP Deactivation"
                isLoading={!!loading || actionItemsLoading}
                headingLevel={2}
              >
                <ShimmeredDetailsListWrapper
                  columns={getColumns(companyCode, EntityTerminationUriSegment.FinanceIncomplete)}
                  items={sapActionItems}
                  singularListLabel="Action Item"
                  pluralListLabel="Action Items"
                />
              </Card>
            </Stack>

            <Stack>
              <ReviewComponent
                review={financeIncompleteReview?.review}
                approveMutation={APPROVE_ENTITY_TERMINATION_FINANCE_INCOMPLETE_REVIEW}
                loading={reviewLoading}
                createCommentMutation={CREATE_ENTITY_TERMINATION_FINANCE_INCOMPLETE_REVIEW_COMMENT}
                attachDocumentMutation={
                  ATTACH_ENTITY_TERMINATION_FINANCE_INCOMPLETE_REVIEW_DOCUMENT
                }
                documentAccessPolicy="EntityTermination:ReviewContribute"
                commentAccessPolicy="EntityTermination:ReviewContribute"
                onApprovalOverrideMethod={(): void => setIsReviewApprovalPanelOpen(true)}
                displayOnly={!financeIncompleteReview?.hasActiveFinanceIncompleteReview}
                customActionButtons={[
                  {
                    label: 'Approve',
                    accessPolicy: 'EntityTerminationFinanceIncompleteReview:Approval',
                    appearance: 'primary',
                    disabled: !allActionItemsCompleted,
                    tooltip: approveDisabledTooltip,
                    onClick: (): void => setIsReviewApprovalPanelOpen(true),
                  },
                ]}
              />
              {isReviewApprovalPanelOpen && financeIncompleteReview && (
                <FinanceIncompleteReviewApprovalPanel
                  closePanel={(): void => setIsReviewApprovalPanelOpen(false)}
                  data={financeIncompleteReview}
                  actionItems={financeIncompleteActionItems}
                />
              )}
            </Stack>
          </Stack>
        </div>
      </FeatureFlagged>
    </>
  );
};

export default EntityTerminationFinanceIncompletePage;
